import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';

const workflowSeed = [
  {
    id: 1,
    name: 'Standard Purchase Order Approval',
    type: 'Purchase Order',
    status: 'Active',
    description: 'Default purchase order approval flow for standard maintenance purchases.',
    conditions: {
      purchaseValueRange: '$0 - $10,000',
      site: 'All Sites',
      department: 'Maintenance',
      formType: 'N/A',
    },
    steps: [
      {
        id: 'w1-s1',
        name: 'Supervisor Approval',
        approverType: 'Role',
        assignmentType: 'Role',
        selectedAssignee: 'Maintenance Supervisor',
        approvalMode: 'Any one approver',
        conditionSummary: 'Always required',
        notifications: { inApp: true, email: true, sms: false },
        requireCommentsOnRejection: true,
        allowReturnForChanges: true,
      },
      {
        id: 'w1-s2',
        name: 'Maintenance Manager Approval',
        approverType: 'Role',
        assignmentType: 'Department Manager',
        selectedAssignee: 'Maintenance Department',
        approvalMode: 'Any one approver',
        conditionSummary: 'Required when total exceeds $2,000',
        notifications: { inApp: true, email: true, sms: false },
        requireCommentsOnRejection: true,
        allowReturnForChanges: true,
      },
    ],
  },
  {
    id: 2,
    name: 'High Value Purchase Order Approval',
    type: 'Purchase Order',
    status: 'Draft',
    description: 'Escalated workflow for larger purchase orders.',
    conditions: {
      purchaseValueRange: '$10,001 - $250,000',
      site: 'QLD',
      department: 'All Departments',
      formType: 'N/A',
    },
    steps: [
      {
        id: 'w2-s1',
        name: 'Supervisor Approval',
        approverType: 'Role',
        assignmentType: 'Role',
        selectedAssignee: 'Site Supervisor',
        approvalMode: 'Any one approver',
        conditionSummary: 'Always required',
        notifications: { inApp: true, email: true, sms: false },
        requireCommentsOnRejection: true,
        allowReturnForChanges: false,
      },
      {
        id: 'w2-s2',
        name: 'Regional Manager Approval',
        approverType: 'Role',
        assignmentType: 'Role',
        selectedAssignee: 'Regional Manager',
        approvalMode: 'All approvers',
        conditionSummary: 'Required for regional budget',
        notifications: { inApp: true, email: true, sms: true },
        requireCommentsOnRejection: true,
        allowReturnForChanges: false,
      },
      {
        id: 'w2-s3',
        name: 'Finance Approval',
        approverType: 'Role',
        assignmentType: 'Cost Centre Owner',
        selectedAssignee: 'Finance Team',
        approvalMode: 'Any one approver',
        conditionSummary: 'Required above $25,000',
        notifications: { inApp: true, email: true, sms: false },
        requireCommentsOnRejection: true,
        allowReturnForChanges: false,
      },
    ],
  },
  {
    id: 3,
    name: 'Safety Form Sign Off',
    type: 'Form',
    status: 'Active',
    description: 'Sign-off workflow for safety-related forms before closure.',
    conditions: {
      purchaseValueRange: 'N/A',
      site: 'All Sites',
      department: 'HSE',
      formType: 'Safety Observation',
    },
    steps: [
      {
        id: 'w3-s1',
        name: 'Supervisor Review',
        approverType: 'Role',
        assignmentType: 'Department Manager',
        selectedAssignee: 'Supervisor',
        approvalMode: 'Any one approver',
        conditionSummary: 'Always required',
        notifications: { inApp: true, email: false, sms: false },
        requireCommentsOnRejection: true,
        allowReturnForChanges: true,
      },
      {
        id: 'w3-s2',
        name: 'HSE Manager Sign Off',
        approverType: 'Role',
        assignmentType: 'Role',
        selectedAssignee: 'HSE Manager',
        approvalMode: 'All approvers',
        conditionSummary: 'Required for incident severity >= Medium',
        notifications: { inApp: true, email: true, sms: false },
        requireCommentsOnRejection: true,
        allowReturnForChanges: true,
      },
    ],
  },
  {
    id: 4,
    name: 'Incident Report Approval',
    type: 'Form',
    status: 'Draft',
    description: 'Workflow for validating and approving incident reports.',
    conditions: {
      purchaseValueRange: 'N/A',
      site: 'Wilpinjong',
      department: 'Operations',
      formType: 'Incident Report',
    },
    steps: [
      {
        id: 'w4-s1',
        name: 'Operations Manager Review',
        approverType: 'Role',
        assignmentType: 'Role',
        selectedAssignee: 'Operations Manager',
        approvalMode: 'Any one approver',
        conditionSummary: 'Always required',
        notifications: { inApp: true, email: true, sms: false },
        requireCommentsOnRejection: true,
        allowReturnForChanges: true,
      },
      {
        id: 'w4-s2',
        name: 'Compliance Approval',
        approverType: 'Role',
        assignmentType: 'Specific User',
        selectedAssignee: 'Compliance Officer',
        approvalMode: 'All approvers',
        conditionSummary: 'Required for report category: Compliance',
        notifications: { inApp: true, email: true, sms: true },
        requireCommentsOnRejection: true,
        allowReturnForChanges: false,
      },
    ],
  },
];

const ApprovalWorkflowConfigurationLayer = () => {
  const [workflows, setWorkflows] = useState(workflowSeed);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(workflowSeed[0]?.id ?? null);
  const [selectedStepId, setSelectedStepId] = useState(workflowSeed[0]?.steps[0]?.id ?? null);
  const [feedback, setFeedback] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const selectedWorkflow = useMemo(
    () => workflows.find((workflow) => workflow.id === selectedWorkflowId) ?? null,
    [workflows, selectedWorkflowId]
  );

  const selectedStep = useMemo(
    () => selectedWorkflow?.steps.find((step) => step.id === selectedStepId) ?? null,
    [selectedWorkflow, selectedStepId]
  );

  const showMockMessage = (message) => {
    setFeedback(message);
    window.setTimeout(() => setFeedback(''), 2200);
  };

  const handleWorkflowSelect = (workflowId) => {
    const workflow = workflows.find((item) => item.id === workflowId);
    setSelectedWorkflowId(workflowId);
    setSelectedStepId(workflow?.steps[0]?.id ?? null);
  };

  const handleStepSelect = (stepId) => setSelectedStepId(stepId);

  const handleAddStep = () => {
    if (!selectedWorkflow) return;
    const newStepNumber = selectedWorkflow.steps.length + 1;
    const newStep = {
      id: `w${selectedWorkflow.id}-s${Date.now()}`,
      name: `New Approval Step ${newStepNumber}`,
      approverType: 'Role',
      assignmentType: 'Role',
      selectedAssignee: 'Approver Role',
      approvalMode: 'Any one approver',
      conditionSummary: 'No condition',
      notifications: { inApp: true, email: false, sms: false },
      requireCommentsOnRejection: false,
      allowReturnForChanges: true,
    };

    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflow.id === selectedWorkflow.id
          ? { ...workflow, steps: [...workflow.steps, newStep] }
          : workflow
      )
    );
    setSelectedStepId(newStep.id);
    showMockMessage('Mock step added to workflow.');
  };

  const updateStepField = (field, value) => {
    if (!selectedWorkflow || !selectedStep) return;
    setWorkflows((prev) =>
      prev.map((workflow) => {
        if (workflow.id !== selectedWorkflow.id) return workflow;
        return {
          ...workflow,
          steps: workflow.steps.map((step) => (step.id === selectedStep.id ? { ...step, [field]: value } : step)),
        };
      })
    );
  };

  const updateNotificationField = (field, value) => {
    if (!selectedWorkflow || !selectedStep) return;
    setWorkflows((prev) =>
      prev.map((workflow) => {
        if (workflow.id !== selectedWorkflow.id) return workflow;
        return {
          ...workflow,
          steps: workflow.steps.map((step) =>
            step.id === selectedStep.id
              ? { ...step, notifications: { ...step.notifications, [field]: value } }
              : step
          ),
        };
      })
    );
  };

  return (
    <>
      {feedback && (
        <div className='alert alert-success d-flex align-items-center gap-2 mb-16' role='alert'>
          <Icon icon='mdi:check-circle-outline' className='text-lg' />
          <span>{feedback}</span>
        </div>
      )}

      <div className='row g-3'>
        <div className='col-12 col-xl-3'>
          <div className='card h-100'>
            <div className='card-header d-flex flex-wrap justify-content-between gap-2'>
              <h6 className='mb-0'>Workflows</h6>
            </div>
            <div className='card-body d-flex flex-column gap-12'>
              <div className='d-flex flex-wrap gap-2'>
                <button type='button' className='btn btn-sm btn-primary-600' onClick={() => showMockMessage('Mock: New workflow started.')}>New Workflow</button>
                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => showMockMessage('Mock: Workflow duplicated.')}>Duplicate</button>
                <button type='button' className='btn btn-sm btn-outline-danger' onClick={() => showMockMessage('Mock: Workflow archived.')}>Archive</button>
              </div>
              <div className='list-group'>
                {workflows.map((workflow) => {
                  const isActive = workflow.id === selectedWorkflowId;
                  return (
                    <button
                      key={workflow.id}
                      type='button'
                      onClick={() => handleWorkflowSelect(workflow.id)}
                      className={`list-group-item list-group-item-action text-start ${isActive ? 'active' : ''}`}
                    >
                      <div className='d-flex justify-content-between align-items-start gap-2'>
                        <div>
                          <div className='fw-semibold'>{workflow.name}</div>
                          <div className={`badge mt-1 ${workflow.status === 'Active' ? 'bg-success-600' : 'bg-warning-600'}`}>{workflow.status}</div>
                        </div>
                        <small>{workflow.steps.length} steps</small>
                      </div>
                      <div className='small mt-1'>Type: {workflow.type}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          {selectedWorkflow ? (
            <div className='d-flex flex-column gap-3'>
              <div className='card'>
                <div className='card-header'>
                  <h6 className='mb-0'>Workflow Details</h6>
                </div>
                <div className='card-body'>
                  <div className='row g-3'>
                    <div className='col-md-6'>
                      <label className='form-label'>Workflow Name</label>
                      <input className='form-control' value={selectedWorkflow.name} readOnly />
                    </div>
                    <div className='col-md-3'>
                      <label className='form-label'>Applies To</label>
                      <input className='form-control' value={selectedWorkflow.type} readOnly />
                    </div>
                    <div className='col-md-3'>
                      <label className='form-label'>Status</label>
                      <input className='form-control' value={selectedWorkflow.status} readOnly />
                    </div>
                    <div className='col-12'>
                      <label className='form-label'>Description</label>
                      <textarea className='form-control' rows={2} value={selectedWorkflow.description} readOnly />
                    </div>
                  </div>
                </div>
              </div>

              <div className='card'>
                <div className='card-header'>
                  <h6 className='mb-0'>Workflow Conditions</h6>
                </div>
                <div className='card-body'>
                  <div className='row g-3'>
                    <div className='col-md-6'>
                      <label className='form-label'>Purchase Value Range</label>
                      <input className='form-control' value={selectedWorkflow.conditions.purchaseValueRange} readOnly />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label'>Site</label>
                      <input className='form-control' value={selectedWorkflow.conditions.site} readOnly />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label'>Department</label>
                      <input className='form-control' value={selectedWorkflow.conditions.department} readOnly />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label'>Form Type</label>
                      <input className='form-control' value={selectedWorkflow.conditions.formType} readOnly />
                    </div>
                  </div>
                </div>
              </div>

              <div className='card'>
                <div className='card-header d-flex flex-wrap gap-2 justify-content-between'>
                  <h6 className='mb-0'>Workflow Builder</h6>
                  <div className='d-flex flex-wrap gap-2'>
                    <button type='button' className='btn btn-sm btn-primary-600' onClick={handleAddStep}>Add Step</button>
                    <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => showMockMessage('Mock: condition editor opened.')}>Add Condition</button>
                    <button type='button' className='btn btn-sm btn-outline-info' onClick={() => setShowPreview(true)}>Preview Workflow</button>
                    <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => showMockMessage('Mock draft saved locally.')}>Save Draft</button>
                    <button type='button' className='btn btn-sm btn-success' onClick={() => showMockMessage('Mock workflow activated.')}>Activate Workflow</button>
                  </div>
                </div>
                <div className='card-body'>
                  <div className='border rounded p-3 bg-light mb-3 text-center fw-semibold'>Start</div>
                  {selectedWorkflow.steps.map((step, index) => {
                    const isStepActive = step.id === selectedStepId;
                    return (
                      <div key={step.id}>
                        <div className='text-center my-2 text-muted'>
                          <Icon icon='mdi:arrow-down' />
                        </div>
                        <button
                          type='button'
                          className={`card w-100 border ${isStepActive ? 'border-primary shadow-sm' : 'border-light'}`}
                          onClick={() => handleStepSelect(step.id)}
                        >
                          <div className='card-body text-start'>
                            <div className='fw-semibold'>Step {index + 1}: {step.name}</div>
                            <div className='small text-muted'>Approver Type: {step.approverType}</div>
                            <div className='small text-muted'>Approval Mode: {step.approvalMode}</div>
                            <div className='small text-muted'>Condition: {step.conditionSummary}</div>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                  <div className='text-center my-2 text-muted'>
                    <Icon icon='mdi:arrow-down' />
                  </div>
                  <div className='border rounded p-3 bg-success-50 text-center fw-semibold'>Approved</div>
                </div>
              </div>
            </div>
          ) : (
            <div className='card'>
              <div className='card-body text-center py-5'>No workflow selected.</div>
            </div>
          )}
        </div>

        <div className='col-12 col-xl-3'>
          <div className='card h-100'>
            <div className='card-header'>
              <h6 className='mb-0'>Step Configuration</h6>
            </div>
            <div className='card-body'>
              {selectedStep ? (
                <div className='d-flex flex-column gap-3'>
                  <div>
                    <label className='form-label'>Step Name</label>
                    <input className='form-control' value={selectedStep.name} onChange={(event) => updateStepField('name', event.target.value)} />
                  </div>
                  <div>
                    <label className='form-label'>Approver Assignment Type</label>
                    <select className='form-select' value={selectedStep.assignmentType} onChange={(event) => updateStepField('assignmentType', event.target.value)}>
                      <option>Specific User</option>
                      <option>Role</option>
                    </select>
                  </div>
                  <div>
                    <label className='form-label'>Selected Role or User</label>
                    <input className='form-control' value={selectedStep.selectedAssignee} onChange={(event) => updateStepField('selectedAssignee', event.target.value)} />
                  </div>
                  <div>
                    <label className='form-label'>Approval Mode</label>
                    <select className='form-select' value={selectedStep.approvalMode} onChange={(event) => updateStepField('approvalMode', event.target.value)}>
                      <option>Any one approver</option>
                      <option>All approvers</option>
                    </select>
                  </div>
                  <div>
                    <label className='form-label d-block'>Notification Options</label>
                    <div className='form-check'>
                      <input className='form-check-input' type='checkbox' checked={selectedStep.notifications.inApp} onChange={(event) => updateNotificationField('inApp', event.target.checked)} id='notify-inapp' />
                      <label className='form-check-label ms-8' htmlFor='notify-inapp'>In app</label>
                    </div>
                    <div className='form-check'>
                      <input className='form-check-input' type='checkbox' checked={selectedStep.notifications.email} onChange={(event) => updateNotificationField('email', event.target.checked)} id='notify-email' />
                      <label className='form-check-label ms-8' htmlFor='notify-email'>Email</label>
                    </div>
                    <div className='form-check'>
                      <input className='form-check-input' type='checkbox' checked={selectedStep.notifications.sms} onChange={(event) => updateNotificationField('sms', event.target.checked)} id='notify-sms' />
                      <label className='form-check-label ms-8' htmlFor='notify-sms'>SMS</label>
                    </div>
                  </div>
                  <div className="form-switch d-inline-flex align-items-center">
                    <input className='form-check-input' type='checkbox' checked={selectedStep.requireCommentsOnRejection} onChange={(event) => updateStepField('requireCommentsOnRejection', event.target.checked)} id='require-comments' />
                    <label className='form-check-label ms-8' htmlFor='require-comments'>Require comments on rejection</label>
                  </div>
                  <div className="form-switch d-inline-flex align-items-center">
                    <input className='form-check-input' type='checkbox' checked={selectedStep.allowReturnForChanges} onChange={(event) => updateStepField('allowReturnForChanges', event.target.checked)} id='allow-return' />
                    <label className='form-check-label ms-8' htmlFor='allow-return'>Allow return for changes</label>
                  </div>
                  <div className='d-flex gap-2'>
                    <button type='button' className='btn btn-primary-600 flex-grow-1' onClick={() => showMockMessage('Mock: step configuration saved.')}>Save Step</button>
                    <button type='button' className='btn btn-outline-danger' onClick={() => showMockMessage('Mock: delete step action.')}>Delete Step</button>
                  </div>
                </div>
              ) : (
                <div className='text-muted'>Select a step from the builder to configure settings.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showPreview && selectedWorkflow && (
        <div className='modal d-block' tabIndex='-1' role='dialog'>
          <div className='modal-dialog modal-lg modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Workflow Preview (Mock)</h5>
                <button type='button' className='btn-close' onClick={() => setShowPreview(false)} />
              </div>
              <div className='modal-body'>
                <p className='mb-3'>
                  <strong>{selectedWorkflow.name}</strong> - {selectedWorkflow.type}
                </p>
                <ul className='mb-0'>
                  {selectedWorkflow.steps.map((step, index) => (
                    <li key={step.id}>Step {index + 1}: {step.name} ({step.approvalMode})</li>
                  ))}
                </ul>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={() => setShowPreview(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovalWorkflowConfigurationLayer;
